/**
 * PERIODE — konteks bulan yang dipilih di bilah atas (`5:58`).
 *
 * MENGAPA BERKAS INI ADA
 * ----------------------
 * Figma menggambar pemilih periode `September 2026 ▾` di bilah atas SETIAP frame, dan
 * kode tidak pernah memilikinya. Memasangnya sebagai pil yang tidak melakukan apa-apa
 * adalah persis kelas cacat yang `tools/ci/gerbang-janji-kendali.mjs` dipasang untuk
 * melarang. Jadi ia dipasang dengan rutenya, dan daftar di bawah adalah yang membuat
 * kejujurannya dapat diperiksa siapa pun.
 *
 * TIGA LAYAR YANG BENAR-BENAR MEMBACA PERIODE, DAN KENAPA HANYA TIGA
 * ------------------------------------------------------------------
 * Bukan pilihan selera: ketiganya adalah layar yang RUTENYA menerima periode DAN yang
 * tidak sudah punya kendali periodenya sendiri.
 *
 *   · `/dashboard`           → `GET /v1/dashboard?period=YYYY-MM`
 *   · `/meetings/escalation` → `GET /v1/spotlight?period_month=YYYY-MM-01`
 *   · `/points`              → `GET /v1/leaderboard?period_month=YYYY-MM-01`
 *
 * `/reports` SENGAJA TIDAK ADA di daftar ini meski `GET /v1/metrics` menerima `from` dan
 * `to`. Layar itu sudah punya dua medan tanggal sendiri, dan rentangnya lebih halus
 * daripada bulan: sebuah pemilih bulan di atasnya adalah kendali KEDUA atas hal yang
 * sama, dan dua kendali atas satu nilai selalu berakhir dengan satu yang diam-diam
 * menang. Ia karena itu dimatikan di `/reports` seperti di layar lain — dan
 * keterangannya berdiri di samping medan tanggal yang memang bekerja.
 *
 * Di layar KONSOL TENANT sisanya periode TIDAK berarti apa-apa — daftar pengguna tidak punya
 * bulan, dan sebuah pengaturan akun tidak berubah karena Agustus. Di sana pemilihnya
 * DIMATIKAN dengan keterangan yang berdiri tanpa perlu ditekan, karena itu satu-satunya
 * bentuk yang jujur: menyembunyikannya membuat bilah atas berubah bentuk dari layar ke
 * layar (dan Figma tidak pernah menggambarnya begitu), sementara membiarkannya hidup di
 * atas layar yang mengabaikannya adalah kendali yang berbohong.
 *
 * DI KONSOL PLATFORM IA TIDAK DIRENDER SAMA SEKALI — cacat 2, 28 September 2026. Aturan
 * "mati dengan keterangan" berlaku tempat pemilihnya KADANG aktif; di konsol platform ia
 * TIDAK PERNAH aktif, dan yang tersisa hanyalah kendali mati permanen plus satu baris teks
 * yang menggantung. Lihat `periodeDitampilkan()` di bawah.
 *
 * MENAMBAH LAYAR KE DAFTAR INI berarti rutenya sudah menerima periode DAN layarnya sudah
 * mengirimkannya. Menambahkannya lebih dulu akan membuat pemilihnya hidup di atas layar
 * yang tidak berubah — kebohongan yang lebih halus daripada pil mati, dan lebih sulit
 * dilihat.
 */

/** Rute yang BENAR-BENAR mengirim periode ke rutenya. Lihat kepala berkas. */
export const RUTE_BERPERIODE: readonly string[] = [
  '/dashboard',
  '/meetings/escalation',
  '/points',
] as const;

export function rutePakaiPeriode(rute: string): boolean {
  return RUTE_BERPERIODE.includes(rute);
}

/**
 * KONSOL PLATFORM — tempat pemilih periode TIDAK DIRENDER SAMA SEKALI (cacat 2).
 *
 * TEMUAN PEMILIK PROYEK, 28 September 2026, dan kenapa ia BUKAN pembatalan aturan di atas
 * -------------------------------------------------------------------------------------
 * Aturan "dimatikan dengan keterangan" dibuat untuk KONSOL TENANT, dan di sana ia tetap
 * benar: pemilihnya KADANG aktif dan kadang tidak, jadi menyembunyikannya membuat bilah
 * atas berubah bentuk dari layar ke layar — dan Figma menggambarnya ada di setiap frame
 * konsol tenant.
 *
 * Di konsol platform ia TIDAK PERNAH aktif. Tidak satu pun dari kelima layarnya punya rute
 * yang menerima periode, dan tidak akan punya: sebuah daftar organisasi tidak punya bulan,
 * dan kesehatan sistem adalah keadaan SEKARANG. Yang tersisa di sana karena itu hanyalah
 * kendali mati permanen ditambah satu baris teks yang menggantung di setiap layar — dua
 * elemen yang tidak menjelaskan apa pun, dan yang tidak digambar frame platform mana pun.
 * "Tidak pernah" adalah keadaan yang berbeda dari "kadang", dan keduanya karena itu
 * memperoleh bentuk yang berbeda.
 *
 * AWALAN, BUKAN DAFTAR KELIMA RUTENYA, dan itu keputusan yang perlu disebut: layar DETAIL
 * organisasi (`/platform/tenants/:id`) juga konsol platform, dan sebuah daftar yang hanya
 * menyebut kelima rute daftar akan membiarkan pemilihnya muncul kembali di satu layar dari
 * enam. Ketidakkonsistenan satu-dari-enam adalah yang paling sulit dilihat.
 *
 * `/portfolio` (konsol KONSULTAN) SENGAJA TIDAK termasuk: seorang konsultan berpindah
 * antara layar berperiode dan layar tanpa periode di dalam satu sesi, jadi aturan konsol
 * tenant berlaku baginya.
 */
export function periodeDitampilkan(rute: string): boolean {
  return !(rute === '/platform' || rute.startsWith('/platform/'));
}

/** `YYYY-MM` untuk sebuah tanggal, di zona pemakai. */
export function bulanDari(d: Date): string {
  const b = `${d.getMonth() + 1}`.padStart(2, '0');
  return `${d.getFullYear()}-${b}`;
}

/** Benar untuk `YYYY-MM` yang bulannya 01..12. */
export function periodeSah(nilai: string): boolean {
  if (!/^\d{4}-\d{2}$/.test(nilai)) return false;
  const bulan = Number(nilai.slice(5, 7));
  return bulan >= 1 && bulan <= 12;
}

/** `YYYY-MM` → `YYYY-MM-01`, bentuk yang dituntut `period_month` di M07 dan M10. */
export function awalBulan(periode: string): string {
  return `${periode}-01`;
}

/**
 * `YYYY-MM` → tanggal terakhir bulan itu, atau HARI INI bila periodenya bulan berjalan.
 *
 * Bedanya bukan kosmetik: `GET /v1/metrics?to=2026-09-30` yang dijalankan pada 28
 * September meminta dua hari yang belum terjadi, dan grafik yang menggambar dua hari nol
 * di ujung kanan menyatakan "kegiatan berhenti" alih-alih "bulannya belum selesai".
 */
export function akhirBulan(periode: string, hariIni = new Date()): string {
  const tahun = Number(periode.slice(0, 4));
  const bulan = Number(periode.slice(5, 7));
  const akhir = new Date(tahun, bulan, 0);
  const iso = `${akhir.getFullYear()}-${`${akhir.getMonth() + 1}`.padStart(2, '0')}-${`${akhir.getDate()}`.padStart(2, '0')}`;
  const hariIniIso = `${hariIni.getFullYear()}-${`${hariIni.getMonth() + 1}`.padStart(2, '0')}-${`${hariIni.getDate()}`.padStart(2, '0')}`;
  return iso > hariIniIso ? hariIniIso : iso;
}

/**
 * Nama bulan dalam bahasa sesi — `September 2026` / `Agustus 2026`.
 *
 * Lewat `Intl`, BUKAN lewat katalog i18n, dan itu keputusan yang disengaja: nama bulan
 * adalah DATA kalender, bukan naskah produk. Menaruh dua belas nama kali dua bahasa di
 * katalog berarti dua belas kesempatan berpisah dari kalender, dan tidak satu pun dari
 * keduanya yang akan pernah dibaca ulang.
 */
export function labelPeriode(periode: string, locale: string): string {
  const tahun = Number(periode.slice(0, 4));
  const bulan = Number(periode.slice(5, 7));
  const d = new Date(Date.UTC(tahun, bulan - 1, 1));
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d);
}

/**
 * Dua belas bulan ke belakang dari bulan berjalan, terbaru lebih dulu.
 *
 * Dua belas dan bukan "semua sejak organisasi lahir": daftar yang tumbuh tanpa batas
 * menjadi tidak dapat dipakai pada tahun kedua, dan tidak ada satu pun laporan di produk
 * ini yang membandingkan lebih dari setahun ke belakang.
 */
export function pilihanPeriode(hariIni = new Date(), jumlah = 12): string[] {
  const hasil: string[] = [];
  for (let i = 0; i < jumlah; i += 1) {
    hasil.push(bulanDari(new Date(hariIni.getFullYear(), hariIni.getMonth() - i, 1)));
  }
  return hasil;
}
